"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TutorialRoomScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const settings_1 = require("../config/settings");
const interfaces_1 = require("../../../shared/interfaces");
const blocks_1 = require("../blocks");
const Global_1 = require("../Global");
const WigglyText_1 = require("../objects/WigglyText");
const i18next_1 = __importDefault(require("i18next"));
const gsap_1 = require("gsap");
const BattleSprite_1 = require("../objects/BattleSprite");
const ColorItem_1 = require("../blocks/ColorItem");
class TutorialRoomScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager, settings_1.Settings.battleColor);
        this.zIndex = SceneManager_1.Layer.Bottom;
        this.musicVolume = 0;
        this.welcomeText = this.makeText(i18next_1.default.t('tutorial.welcome'));
        this.titleText = this.makeText(settings_1.Settings.title + '!');
        this.titleText.visible = false;
        if (this.skipWelcome) {
            this.welcomeText.visible = false;
        }
        this.footerText = this.makeFooterText('typeinfo.' + interfaces_1.GameType.Tutorial);
        this.footerText.alpha = 0;
        this.footerText.position.y = this.app.screen.height;
        this.splash = this.addChild(new ColorItem_1.ColorItem(this.app, settings_1.Settings.splashColor));
        this.splash.visible = false;
        this.sprite = this.addChild(new BattleSprite_1.BattleSprite(this.app));
        this.subs.add(this.gameManager.room$.subscribe({
            next: (room) => __awaiter(this, void 0, void 0, function* () {
                if ((room === null || room === void 0 ? void 0 : room.type) === interfaces_1.GameType.Tutorial && !this.isDeinited) {
                    this.gameManager.notifyReady();
                }
            })
        }));
    }
    makeText(text) {
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 64 : 52);
        style.align = 'center';
        style.padding = (0, Global_1.px)(12);
        const wiggle = new WigglyText_1.WigglyText(this.app, text, style);
        wiggle.power = (0, Global_1.px)(7);
        wiggle.anchor.set(0.5, 1);
        return this.addChild(wiggle);
    }
    get skipWelcome() {
        const hash = window.location.hash;
        return (0, Global_1.isPoki)() || hash === '#draw';
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield this.animateFall();
            return _super.exitScene.call(this, to, target);
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const size = (0, Global_1.px)(10);
            yield this.animateMask(new pixi_js_1.Rectangle(this.center.x - size / 2, this.center.y - size / 2, size, size), false, 1);
            return _super.enterScene.call(this, from, target);
        });
    }
    animate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.titleText.visible = true;
            this.titleText.scale.set(0.2);
            this.titleText.rotation = 1;
            const p1 = this.promisedTimeline(gsap_1.gsap.timeline()
                .to(this.titleText, {
                pixi: {
                    scale: 1.6,
                    rotation: 10
                },
                ease: 'power2.out',
                duration: 0.3
            }, '<')
                .to(this.titleText, {
                pixi: {
                    scale: 1.0,
                    rotation: 0
                },
                ease: 'power2.in',
                duration: 0.3
            }, '>'));
            const p2 = this.animateFooterText(this.footerText);
            yield Promise.all([p1, p2]);
            this.sprite.finish = () => {
                this.splash.visible = true;
            };
            this.sprite.play();
            yield (0, Global_1.sleep)(1.5);
            yield this.promisedTimeline(gsap_1.gsap.timeline()
                .to(this.welcomeText, {
                pixi: {
                    scale: 0,
                    alpha: 0,
                    rotation: 10
                },
                duration: 0.25,
                onComplete: () => {
                    this.welcomeText.text = i18next_1.default.t('tutorial.guess1');
                }
            }, 'head')
                .to(this.welcomeText, {
                pixi: {
                    scale: 1,
                    alpha: 1,
                    rotation: 0
                },
                duration: 0.25
            }, '>head')
                .to(this.titleText, {
                pixi: {
                    scale: 0,
                    alpha: 0,
                    rotation: -10
                },
                duration: 0.25,
                onComplete: () => {
                    this.titleText.text = i18next_1.default.t('tutorial.guess2');
                }
            }, 'head+10%')
                .to(this.titleText, {
                pixi: {
                    scale: 1,
                    alpha: 1,
                    rotation: 0
                },
                duration: 0.25
            }, '>'));
        });
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const booter = () => {
                if (!this.isDeinited) {
                    this.gameManager.enterRoom(interfaces_1.GameType.Tutorial);
                }
            };
            if (this.skipWelcome) {
                booter();
            }
            else {
                this.animate().then(() => (0, Global_1.sleep)(2)).then(booter);
            }
            yield _super.init.call(this);
        });
    }
    layout() {
        super.layout();
        this.welcomeText.style.fontSize = this.titleText.style.fontSize = (0, Global_1.fontSize)(this.overSmallSize ? 64 : 52);
        this.welcomeText.position.set(this.center.x, this.isLandscapeBox ? (0, Global_1.px)(70) : this.app.screen.height / 3);
        this.titleText.position.set(this.center.x, this.welcomeText.y + this.welcomeText.height + (0, Global_1.px)(24));
        this.sprite.scale.set(this.isLandscapeBox ? 1.8 : 2.2);
        this.sprite.position.set(this.center.x, this.titleText.y + (0, Global_1.px)(this.isLandscapeBox ? 5 : 40));
        this.splash.scale.set(1.5);
        this.splash.position.set(this.center.x - this.splash.width / 2, this.sprite.y + this.sprite.height - this.splash.width / 2);
        this.footerText.position.x = this.center.x;
    }
}
exports.TutorialRoomScene = TutorialRoomScene;
